import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'

import { Course, CourseUser, IntegrationStatus, Provider } from 'graphqlAPI'
import StateMonitor from 'components/ui/state-monitor/StateMonitor'
import { AvatarWithInfo } from 'components/reusables/avatarWithInfo/AvatarWithInfo'
import { COLORS } from 'components/reusables/DesignConstants'
import MissingDc3AttestationIcon from 'components/administration/components/courses/components/courses/main/components/MissingDc3AttestationIcon'
import { createRouteParticipantCourseResults } from 'enum/routes'
import { MonitoringStatus, CourseStateType } from 'enum'
import CustomLink from 'components/ui/CustomLink'
import { EnrolState } from 'enum/enrol-state.enum'
import FinalState from 'components/synchronousCourses/FinalState'
import coursera from 'assets/courseraIndicator.svg'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { useIntegrationStatus } from 'hooks/useIntegrationStatus'
import { useAccess } from 'contexts/AccessContext'
import { IMainState } from 'store/states'
import { Evaluations } from '../../components/Evaluations'
import Options from '../Options'
import { prefix } from '../../constants'
import { prefix as statisticsPrefix } from '../../../constants'
import tableStyles from '../../index.module.scss'
import {
  Column,
  ACTION_COLUMN_WIDTH,
  NAME_COLUMN_WIDTH,
  AREA_COLUMN_WIDTH,
} from './types'
import styles from './useColumns.module.scss'

const prefixHeaders = `${prefix}.headers`
const prefixColumns = `${prefix}.columns`
const normativeIncompleteParticipantTooltipKey = `${statisticsPrefix}.normativeIncompleteParticipantTooltip`

interface useColumnsProps {
  evaluationsCount: number
  course?: Course
  setSelected: React.Dispatch<React.SetStateAction<CourseUser | null>>
}

const ICONS_BY_PROVIDER: {
  [P in Provider]?: string
} = {
  [Provider.Coursera]: coursera,
}

const INTEGRATIONS_KEYS = {
  [Provider.Coursera]: 'configuration.integrations.coursera',
}

export function useColumns({
  evaluationsCount,
  course,
  setSelected,
}: useColumnsProps) {
  const { t } = useTranslation()
  const { access } = useAccess()
  const canViewParticipantResults = access.canViewParticipantResults()
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )

  const {
    lessonsCount = 0,
    integrationCourse,
    normative = false,
  } = {
    ...course,
  }

  const showNormativeIncompleteIcons = attestationsEnabled && normative === true

  const isSynchronous = !!course?.synchronousCourse
  const courseInactive = course?.state === CourseStateType.Inactive

  const integrationStatusTranslations = useIntegrationStatus()

  const liveSessionAttendanceColumn: Column | null =
    course?.hasLiveSessionBlocks
      ? {
          title: t(`${prefixHeaders}.liveSessionAttendance`),
          key: 'live_session_attendance_percent',
          width: 120,
          sorteable: false,
          render: (_, { liveSessionAttendancePercent }: CourseUser) => (
            <div className="text-center">
              {liveSessionAttendancePercent ?? 0}%
            </div>
          ),
        }
      : null

  const columns: Column[] = useMemo(() => {
    const provider = integrationCourse?.provider
    const icon = provider && ICONS_BY_PROVIDER[provider]
    const providerLabel = provider && t(INTEGRATIONS_KEYS[provider])

    const columnsBase: (Column | null | false)[] = [
      {
        title: t(`${prefixHeaders}.name`),
        sorteable: true,
        key: 'user_name',
        hideable: false,
        width: NAME_COLUMN_WIDTH,
        fixed: 'left',
        className: showNormativeIncompleteIcons
          ? styles.nameColumnWithIcon
          : styles.nameColumn,
        render: (
          _,
          {
            user: { name, avatar, lastname, position },
            courseId,
            userId,
            advance = MonitoringStatus.Pending,
            normativeProfileIncomplete = false,
          }: CourseUser
        ) => {
          const hasStarted = advance !== MonitoringStatus.Pending
          const showIcon =
            showNormativeIncompleteIcons && normativeProfileIncomplete === true
          const fullName = [name, lastname].filter(Boolean).join(' ')

          const avatarContent = (
            <AvatarWithInfo
              borderRadius={50}
              icon={faUser}
              image={avatar}
              principalColor={hasStarted ? COLORS.lightBlue : COLORS.content}
              background={
                hasStarted ? COLORS.lightBlue : COLORS.imageBackground
              }
              size={30}
              title={fullName}
              subtitle={position || undefined}
              fontWeight="normal"
            />
          )

          return (
            <div
              className={
                showIcon
                  ? `${styles.nameCell} ${styles.nameCellWithIcon}`
                  : styles.nameCell
              }
            >
              {showIcon ? (
                <MissingDc3AttestationIcon
                  tooltipKey={normativeIncompleteParticipantTooltipKey}
                />
              ) : null}
              {hasStarted && canViewParticipantResults && !courseInactive ? (
                <CustomLink
                  to={createRouteParticipantCourseResults({
                    userId: `${userId}`,
                    courseId,
                  })}
                  className={styles.profileLink}
                >
                  {avatarContent}
                </CustomLink>
              ) : (
                avatarContent
              )}
            </div>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.area`),
        key: 'area_name',
        width: AREA_COLUMN_WIDTH,
        className: styles.areaColumn,
        sorteable: true,
        render: (_, { area }: CourseUser) => (
          <span className={styles.cellEllipsis} title={area?.name}>
            {area?.name}
          </span>
        ),
      },
      !!provider && {
        title: (
          <div className={styles.integration}>
            {t(`${prefixHeaders}.integrationStatus`)}
            <CommonToolTip text={providerLabel} position="bottom">
              {icon && (
                <img src={icon} alt={provider} className={styles.icon} />
              )}
            </CommonToolTip>
          </div>
        ),
        key: 'enrol_integration_status',
        width: 150,
        sorteable: true,
        render: (_, { enrol }) => {
          const key = enrol?.integrationStatus || IntegrationStatus.Pending
          return <div>{integrationStatusTranslations[key]}</div>
        },
      },
      {
        title: t(`${prefixHeaders}.state`),
        key: 'advance',
        width: 150,
        sorteable: true,
        render: (_, { advance }) => <StateMonitor state={advance || 0} />,
      },
      {
        title: t(`${prefixHeaders}.progress`),
        key: 'enrol_overall_progress',
        width: 170,
        sorteable: true,
        render: (_, { overallProgress }) => (
          <>{(overallProgress || 0).toFixed(1)}%</>
        ),
      },
      isSynchronous && {
        title: t(`${prefixHeaders}.finalState`),
        key: 'synchronous_course_enrols_final_state',
        width: 120,
        sorteable: true,
        render: (_, { synchronousCourseEnrol }: CourseUser) => {
          const { finalState = EnrolState.Pending } = {
            ...synchronousCourseEnrol,
          }
          return (
            <div className="text-center">
              <FinalState state={finalState as EnrolState} />
            </div>
          )
        },
      },
      !isSynchronous &&
        evaluationsCount > 0 && {
          title: t(`${prefixHeaders}.evaluations`),
          key: 'state_evaluations',
          width: 120,
          sorteable: true,
          render: (_state, courseUser: CourseUser) => {
            // Si evaluationsState es null, el curso no tiene evaluaciones
            if (courseUser.evaluationsState === null) {
              return <div>--</div>
            }

            return (
              <Evaluations
                state={courseUser.evaluationsState}
                courseId={courseUser.courseId}
                userId={courseUser.userId}
                user={{
                  name: courseUser.user?.name,
                  lastname: courseUser.user?.lastname,
                  avatar: courseUser.user?.avatar,
                  position: courseUser.user?.position,
                }}
              />
            )
          },
        },
      ...(isSynchronous
        ? new Array(course?.synchronousCourseEvaluationsCount || 0).fill(0).map(
            (_, index): Column => ({
              title: (
                <div className="text-wrap">
                  {t(`${prefixHeaders}.synchronousCourseEvaluation`, {
                    index: index + 1,
                  })}
                </div>
              ),
              key: `synchronous_course_evaluations_${index}`,
              width: 120,
              sorteable: false,
              render: (_, { synchronousCourseEvaluations }: CourseUser) => {
                const evaluation = synchronousCourseEvaluations?.[index]
                return (
                  <div className="text-center">
                    {evaluation?.qualification || '-'}
                  </div>
                )
              },
            })
          )
        : []),
      isSynchronous && {
        title: t(`${prefixHeaders}.attendance`),
        key: 'synchronous_course_enrol_attendance',
        width: 120,
        sorteable: true,
        render: (_, { synchronousCourseEnrol }: CourseUser) => (
          <div className="text-center">
            {synchronousCourseEnrol?.attendance || 0}%
          </div>
        ),
      },
      liveSessionAttendanceColumn,
      lessonsCount > 0 && {
        title: t(`${prefixHeaders}.lessons`),
        key: 'assignedLessons',
        width: 120,
        sorteable: true,
        render: (_, { assignedLessons }) => (
          <span>
            {t(`${prefixColumns}.lessons`, {
              count: assignedLessons || 0,
              total: lessonsCount,
            })}
          </span>
        ),
      },
      {
        title: '',
        key: 'action',
        dataIndex: 'action',
        width: ACTION_COLUMN_WIDTH,
        hideable: false,
        align: 'center',
        fixed: 'right',
        className: tableStyles.actionColumn,
        render: (id, courseUser: CourseUser) => {
          const { finalState = EnrolState.Pending } = {
            ...courseUser.synchronousCourseEnrol,
          }
          const canEdit = isSynchronous && finalState === EnrolState.Pending

          return (
            <Options
              courseUser={courseUser}
              courseNormative={normative === true}
              courseInactive={courseInactive}
              onFinish={canEdit ? () => setSelected(courseUser) : undefined}
            />
          )
        },
      },
    ]
    return columnsBase.reduce<Column[]>((acc, column) => {
      if (column) acc.push(column)
      return acc
    }, [])
  }, [
    course,
    evaluationsCount,
    integrationCourse,
    integrationStatusTranslations,
    isSynchronous,
    lessonsCount,
    liveSessionAttendanceColumn,
    setSelected,
    showNormativeIncompleteIcons,
    canViewParticipantResults,
    t,
  ])
  return columns
}
